// CSV exports meant for a French spreadsheet: semicolon separator, decimal
// comma, ISO dates, CRLF line ends and a UTF-8 BOM so Excel reads accents.
// Amounts are exported as plain numbers (no currency sign, no thousands
// separator) so the sheet can sum them; the discreet mode does not apply to an
// explicit export.

const SEPARATOR = ';';

const isoDate = (value) => {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const decimal = (value, decimals) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '';
  const n = Number(value);
  const text = decimals === undefined ? String(n) : n.toFixed(decimals);
  return text.replace('.', ',');
};

const quote = (text) => (/[";\r\n]/.test(text) || /^\s|\s$/.test(text) ? `"${text.replace(/"/g, '""')}"` : text);

const cell = (value, type, decimals) => {
  if (type === 'date') return isoDate(value);
  if (type === 'number') return decimal(value, decimals);
  return quote(value === null || value === undefined ? '' : String(value));
};

// columns: [{ label, value: (row) => any, type?: 'text'|'number'|'date', decimals? }]
export const toCsv = (rows, columns) => {
  const header = columns.map((c) => quote(c.label)).join(SEPARATOR);
  const lines = rows.map((row) => columns.map((c) => cell(c.value(row), c.type, c.decimals)).join(SEPARATOR));
  return `﻿${[header, ...lines].join('\r\n')}\r\n`;
};

export const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const today = () => new Date().toISOString().slice(0, 10);

const DIRECTIONS = { Buy: 'Achat', Sell: 'Vente' };

export const CLOSED_POSITION_COLUMNS = [
  { label: 'Symbole', value: (p) => p.symbol },
  { label: 'Libellé', value: (p) => p.description },
  { label: 'Type', value: (p) => p.asset_type },
  { label: 'Sens', value: (p) => DIRECTIONS[p.buy_or_sell] || p.buy_or_sell },
  { label: 'Quantité', value: (p) => p.amount, type: 'number' },
  { label: "Date d'ouverture", value: (p) => p.open_date, type: 'date' },
  { label: 'Date de clôture', value: (p) => p.close_date, type: 'date' },
  { label: 'Durée (jours)', value: (p) => p.holding_period_days, type: 'number' },
  { label: "Prix d'entrée", value: (p) => p.open_price, type: 'number' },
  { label: 'Prix de sortie', value: (p) => p.closing_price, type: 'number' },
  { label: 'Devise', value: (p) => p.currency || 'EUR' },
  { label: 'Résultat', value: (p) => p.closed_profit_loss, type: 'number', decimals: 2 },
  { label: 'Résultat (%)', value: (p) => p.closed_profit_loss_pct, type: 'number', decimals: 2 },
  { label: 'Méthode de clôture', value: (p) => p.closing_method },
];

export const DIVIDEND_COLUMNS = [
  { label: 'Symbole', value: (tx) => tx.Instrument?.Symbol },
  { label: 'Libellé', value: (tx) => tx.Instrument?.Description },
  { label: 'Type', value: (tx) => tx.EventDisplay || tx.Event },
  { label: 'Quantité', value: (tx) => tx.EligibleHoldings, type: 'number' },
  { label: 'Montant', value: (tx) => tx.BookedAmount, type: 'number', decimals: 2 },
  { label: 'Date', value: (tx) => tx.Date, type: 'date' },
  { label: 'Date de paiement', value: (tx) => tx.PayDate, type: 'date' },
];

export const exportClosedPositions = (positions) => downloadCsv(`pea-operations-cloturees-${today()}.csv`, toCsv(positions, CLOSED_POSITION_COLUMNS));

export const exportDividends = (transactions, label) => downloadCsv(`pea-dividendes-${label}.csv`, toCsv(transactions, DIVIDEND_COLUMNS));
